import {
  EndCustomer,
  OrderAcquisitionType,
  RecurrenceType,
  SavedView,
  SavedViewFilterInput,
  SavedViewSortInput,
  ShipmentsSorting,
} from "@api/graphql/generated/generated-types";
import { useShipmentFiltersContext } from "../../ShipmentFiltersContext";
import { useTableControl } from "../../TableControlContext";
import { CustomFilter, ShipmentsPrimaryFilterType } from "../../../types";
import { useAuth } from "@src/common/hooks/useAuth";

export function useFilterMapping() {
  const { courier } = useAuth();

  const {
    selectedPrimaryFilter,
    setSelectedPrimaryFilter,
    selectedCustomFilter,
    setSelectedCustomFilter,
    selectedDriverIdFilter,
    setSelectedDriverIdFilter,
    selectedCustomer,
    setSelectedCustomer,
    trackingNumbersFilter,
    setTrackingNumbersFilter,
    displayIdsFilter,
    setDisplayIdsFilter,
    selectedDriverLabelIds,
    setSelectedDriverLabelIds,
    dispatchDriverLabelIds,
    setDispatchDriverLabelIds,
    selectedOrderLabelIds,
    setSelectedOrderLabelIds,
    selectedEndCustomerLabelIds,
    setSelectedEndCustomerLabelIds,
    selectedCourierBrandIds,
    setSelectedCourierBrandIds,
    selectedAcquisitionTypes,
    setSelectedAcquisitionTypes,
    selectedRecurrenceTypes,
    setSelectedRecurrenceTypes,
    selectedPickupZoneIds,
    setSelectedPickupZoneIds,
    selectedDropoffZoneIds,
    setSelectedDropoffZoneIds,
    selectedPackageTypeIds,
    setSelectedPackageTypeIds,
    selectedOrderVehicleTypeIds,
    setSelectedOrderVehicleTypeIds,
    selectedDriverVehicleTypeIds,
    setSelectedDriverVehicleTypeIds,
    selectedServiceTypeIds,
    setSelectedServiceTypeIds,
    minWeightInPounds,
    setMinWeightInPounds,
    maxWeightInPounds,
    setMaxWeightInPounds,
    selectedRouteTemplateIds,
    setSelectedRouteTemplateIds,
  } = useShipmentFiltersContext();

  // Get custom filters from courier configuration
  const customFilters: CustomFilter[] =
    courier?.courier?.shipmentsCustomFilters?.filters || [];

  const { sortFields, setSortFields } = useTableControl();

  // Build filters array from current context state
  const buildFiltersFromContext = (): SavedViewFilterInput[] => {
    const filters: SavedViewFilterInput[] = [];

    // Save custom filter name when primary filter is CUSTOM
    if (
      selectedPrimaryFilter === ShipmentsPrimaryFilterType.CUSTOM &&
      selectedCustomFilter?.name
    ) {
      filters.push({
        key: "customFilterName",
        values: [selectedCustomFilter.name],
      });
    }

    if (selectedDriverIdFilter) {
      filters.push({ key: "driverId", values: [selectedDriverIdFilter] });
    }
    if (selectedDriverLabelIds.length > 0) {
      filters.push({ key: "driverLabelIds", values: selectedDriverLabelIds });
    }
    if (dispatchDriverLabelIds.length > 0) {
      filters.push({
        key: "dispatchDriverLabelIds",
        values: dispatchDriverLabelIds,
      });
    }
    if (selectedOrderLabelIds.length > 0) {
      filters.push({ key: "orderLabelIds", values: selectedOrderLabelIds });
    }
    if (selectedEndCustomerLabelIds.length > 0) {
      filters.push({ key: "endCustomerLabelIds", values: selectedEndCustomerLabelIds });
    }
    if (selectedCourierBrandIds.length > 0) {
      filters.push({ key: "courierBrandIds", values: selectedCourierBrandIds });
    }
    if (selectedAcquisitionTypes.length > 0) {
      filters.push({ key: "acquisitionTypes", values: selectedAcquisitionTypes });
    }
    if (selectedRecurrenceTypes.length > 0) {
      filters.push({ key: "recurrenceTypes", values: selectedRecurrenceTypes });
    }
    if (selectedPickupZoneIds.length > 0) {
      filters.push({ key: "pickupZoneIds", values: selectedPickupZoneIds });
    }
    if (selectedDropoffZoneIds.length > 0) {
      filters.push({ key: "dropoffZoneIds", values: selectedDropoffZoneIds });
    }
    if (selectedPackageTypeIds.length > 0) {
      filters.push({ key: "packageTypeIds", values: selectedPackageTypeIds });
    }
    if (selectedOrderVehicleTypeIds.length > 0) {
      filters.push({
        key: "orderVehicleTypeIds",
        values: selectedOrderVehicleTypeIds,
      });
    }
    if (selectedDriverVehicleTypeIds.length > 0) {
      filters.push({
        key: "driverVehicleTypeIds",
        values: selectedDriverVehicleTypeIds,
      });
    }
    if (selectedServiceTypeIds.length > 0) {
      filters.push({ key: "serviceTypeIds", values: selectedServiceTypeIds });
    }
    if (selectedCustomer?.id) {
      filters.push({ key: "endCustomerId", values: [selectedCustomer.id] });
      if (selectedCustomer.companyName) {
        filters.push({ key: "endCustomerCompanyName", values: [selectedCustomer.companyName] });
      }
    }
    if (trackingNumbersFilter && trackingNumbersFilter.length > 0) {
      filters.push({ key: "trackingNumbersFilter", values: [trackingNumbersFilter] });
    }
    if (displayIdsFilter !== undefined) {
      filters.push({ key: "displayIdsFilter", values: [String(displayIdsFilter)] });
    }
    if (minWeightInPounds !== undefined) {
      filters.push({ key: "minWeightInPounds", values: [String(minWeightInPounds)] });
    }
    if (maxWeightInPounds !== undefined) {
      filters.push({ key: "maxWeightInPounds", values: [String(maxWeightInPounds)] });
    }
    if (selectedRouteTemplateIds.length > 0) {
      filters.push({ key: "routeTemplateIds", values: selectedRouteTemplateIds });
    }

    return filters;
  };

  // Build sort fields array from current context state
  const buildSortFieldsFromContext = (): SavedViewSortInput[] => {
    return (sortFields as ShipmentsSorting[]).map((sf) => ({
      sortBy: sf.field,
      direction: sf.direction,
    }));
  };

  // Apply saved view to filter and sort contexts
  const applyViewToFilters = (view: SavedView) => {
    // Build filters map
    const filtersMap: Record<string, string[]> = {};
    view.filters.forEach((f) => {
      filtersMap[f.key] = f.values;
    });

    // Apply primary filter
    if (view.primaryFilter) {
      const primaryFilter = view.primaryFilter as ShipmentsPrimaryFilterType;
      setSelectedPrimaryFilter(primaryFilter);

      // Restore custom filter if primary filter is CUSTOM
      if (primaryFilter === ShipmentsPrimaryFilterType.CUSTOM) {
        const customFilterName = filtersMap["customFilterName"]?.[0];
        if (customFilterName) {
          const foundCustomFilter = customFilters.find(
            (cf) => cf.name === customFilterName
          );
          if (foundCustomFilter) {
            setSelectedCustomFilter({
              name: foundCustomFilter.name,
              endCustomerIds: foundCustomFilter.endCustomerIds || undefined,
              serviceTypeIds: foundCustomFilter.serviceTypeIds || undefined,
              shipmentStatuses: foundCustomFilter.shipmentStatuses || undefined,
              excludeWordsFromNote:
                foundCustomFilter.excludeWordsFromNote || undefined,
              driverIds: foundCustomFilter.driverIds || undefined,
              isConfirmed: foundCustomFilter.isConfirmed ?? undefined,
              isAttempted: foundCustomFilter.isAttempted ?? undefined,
              recurrenceTypes: foundCustomFilter.recurrenceTypes ?? undefined,
            });
          } else {
            // Custom filter no longer exists in courier config
            setSelectedCustomFilter(undefined);
          }
        } else {
          setSelectedCustomFilter(undefined);
        }
      } else {
        // Clear custom filter when primary filter is not CUSTOM
        setSelectedCustomFilter(undefined);
      }
    }

    // Apply filters to context
    setSelectedDriverIdFilter(filtersMap["driverId"]?.[0] || undefined);
    setSelectedDriverLabelIds(filtersMap["driverLabelIds"] || []);
    setDispatchDriverLabelIds(filtersMap["dispatchDriverLabelIds"] || []);
    setSelectedOrderLabelIds(filtersMap["orderLabelIds"] || []);
    setSelectedEndCustomerLabelIds(filtersMap["endCustomerLabelIds"] || []);
    setSelectedCourierBrandIds(filtersMap["courierBrandIds"] || []);
    setSelectedAcquisitionTypes((filtersMap["acquisitionTypes"] as OrderAcquisitionType[]) || []);
    setSelectedRecurrenceTypes((filtersMap["recurrenceTypes"] as RecurrenceType[]) || []);
    setSelectedPickupZoneIds(filtersMap["pickupZoneIds"] || []);
    setSelectedDropoffZoneIds(filtersMap["dropoffZoneIds"] || []);
    setSelectedPackageTypeIds(filtersMap["packageTypeIds"] || []);
    setSelectedOrderVehicleTypeIds(filtersMap["orderVehicleTypeIds"] || []);
    setSelectedDriverVehicleTypeIds(filtersMap["driverVehicleTypeIds"] || []);
    setSelectedServiceTypeIds(filtersMap["serviceTypeIds"] || []);

    // Restore end customer if saved
    const endCustomerId = filtersMap["endCustomerId"]?.[0];
    if (endCustomerId) {
      const endCustomerCompanyName = filtersMap["endCustomerCompanyName"]?.[0];
      setSelectedCustomer({
        id: endCustomerId,
        companyName: endCustomerCompanyName || "",
      } as EndCustomer);
    } else {
      setSelectedCustomer(undefined);
    }

    // Restore tracking numbers and display ID filters
    setTrackingNumbersFilter(filtersMap["trackingNumbersFilter"]?.[0] || "");
    const displayIdValue = filtersMap["displayIdsFilter"]?.[0];
    setDisplayIdsFilter(displayIdValue ? Number(displayIdValue) : undefined);
    const minWeightValue = filtersMap["minWeightInPounds"]?.[0];
    setMinWeightInPounds(minWeightValue ? Number(minWeightValue) : undefined);
    const maxWeightValue = filtersMap["maxWeightInPounds"]?.[0];
    setMaxWeightInPounds(maxWeightValue ? Number(maxWeightValue) : undefined);
    setSelectedRouteTemplateIds(filtersMap["routeTemplateIds"] || []);

    // Apply sort fields from saved view only if view has sorts
    // If no sorts in saved view, preserve existing sorts (e.g., from localStorage)
    if (view.sortFields.length > 0) {
      const newSortFields: ShipmentsSorting[] = view.sortFields.map((sf) => ({
        field: sf.sortBy as any,
        direction: sf.direction,
      }));
      setSortFields(newSortFields);
    }
  };

  // Clear all filters and sorts to default values
  const clearFiltersToDefault = () => {
    // Reset primary filter to ACTIVE (default)
    setSelectedPrimaryFilter(ShipmentsPrimaryFilterType.ACTIVE);
    setSelectedCustomFilter(undefined);

    // Clear all filters
    setSelectedDriverIdFilter(undefined);
    setSelectedCustomer(undefined);
    setTrackingNumbersFilter("");
    setDisplayIdsFilter(undefined);
    setSelectedDriverLabelIds([]);
    setDispatchDriverLabelIds([]);
    setSelectedOrderLabelIds([]);
    setSelectedEndCustomerLabelIds([]);
    setSelectedCourierBrandIds([]);
    setSelectedAcquisitionTypes([]);
    setSelectedRecurrenceTypes([]);
    setSelectedPickupZoneIds([]);
    setSelectedDropoffZoneIds([]);
    setSelectedPackageTypeIds([]);
    setSelectedOrderVehicleTypeIds([]);
    setSelectedDriverVehicleTypeIds([]);
    setSelectedServiceTypeIds([]);
    setMinWeightInPounds(undefined);
    setMaxWeightInPounds(undefined);
    setSelectedRouteTemplateIds([]);

    // Clear sort fields
    setSortFields([]);
  };

  return {
    selectedPrimaryFilter,
    buildFiltersFromContext,
    buildSortFieldsFromContext,
    applyViewToFilters,
    clearFiltersToDefault,
  };
}
